import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import WhatsAppFloat from '@/components/WhatsAppFloat'
import ShareButtons from '@/components/ShareButtons'
import Breadcrumb from '@/components/Breadcrumb'
import CTABanner from '@/components/CTABanner'
import MetaTags from '@/components/MetaTags'
import BlogComments from '@/components/BlogComments'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Calendar, User, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface BlogPost {
  title: string
  content: string | null
  author: string
  category: string
  image_url: string | null
  video_url: string | null
  created_at: string
}

const getYouTubeVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}

const getYouTubeThumbnail = (videoUrl: string): string | null => {
  const videoId = getYouTubeVideoId(videoUrl)
  return videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null
}

const getYouTubeEmbedUrl = (videoUrl: string): string | null => {
  const videoId = getYouTubeVideoId(videoUrl)
  return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : null
}

const BlogPost = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (slug) {
      fetchPost(slug)
    }
  }, [slug])

  const fetchPost = async (postSlug: string) => {
    setIsLoading(true)
    const { data, error } = await supabase
      .from('blog_posts')
      .select(
        'title, content, author, category, image_url, video_url, created_at',
      )
      .eq('slug', postSlug)
      .eq('published', true)
      .maybeSingle()

    if (error || !data) {
      setNotFound(true)
    } else {
      setPost(data)
    }
    setIsLoading(false)
  }

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), "d 'de' MMMM, yyyy", { locale: ptBR })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    )
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Post não encontrado</h1>
            <Button onClick={() => navigate('/blog')}>Voltar ao Blog</Button>
          </div>
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    )
  }

  const description = post.content
    ? post.content
        .substring(0, 160)
        .replace(/<[^>]*>/g, '')
        .trim() + '...'
    : 'Leia este artigo no Blog FotoLivro Store'

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title={`${post.title} - Blog FotoLivro Store`}
        description={description}
        image={
          post.image_url ||
          (post.video_url && getYouTubeThumbnail(post.video_url)) ||
          undefined
        }
        type="article"
        author={post.author}
        publishedTime={post.created_at}
      />
      <Header />
      <Breadcrumb />
      <main className="flex-1">
        <article className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <Button
              variant="ghost"
              onClick={() => navigate('/blog')}
              className="mb-8"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar ao Blog
            </Button>

            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
                {post.category}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                {post.title}
              </h1>
              <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-6 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(post.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{post.author}</span>
                  </div>
                </div>
                <ShareButtons
                  title={post.title}
                  text={`Confira este artigo: ${post.title}`}
                />
              </div>
            </div>

            {post.video_url && getYouTubeEmbedUrl(post.video_url) && (
              <div className="w-full aspect-video rounded-lg overflow-hidden mb-8">
                <iframe
                  src={getYouTubeEmbedUrl(post.video_url)!}
                  title={post.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            {!post.video_url && post.image_url && (
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-[400px] object-cover rounded-lg mb-8"
              />
            )}

            {post.content && (
              <div
                className="prose prose-lg max-w-none text-foreground"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            )}

            <CTABanner
              title="Comece Seu Álbum Hoje"
              description="Sua história merece ser contada com sofisticação. Pré-selecione suas fotos e deixe nossa equipe de especialistas transformá-las em um álbum exclusivo, com design profissional e qualidade impecável."
              buttonText="Iniciar meu álbum"
              buttonLink="/#produtos"
            />

            <BlogComments postSlug={slug || ''} />
          </div>
        </article>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  )
}

export default BlogPost
